import { EventsFilter } from '@apis/events';
import LivePollingInstance from '@live/pollingHandler';
import type { MatchUnitServiceOptions } from '@services/MatchUnitService';
import matchUnitServiceInstance from '@services/MatchUnitService';
import { Order } from '@shared-types/common.types';
import type { Match, PlayerEvent } from '@shared-types/match.types';
import type { Statistic } from '@shared-types/stats.types';
import { IS_MOBILE } from '@utilities/browser';
import { formatUrl } from '@utilities/fetch';
import { getRefreshRate, matchLineupsAvailable, matchStatisticsAvailable } from '@utilities/live';
import { apiKey, matchApiUrl, matchStatsApiUrl } from '@utilities/WindowContext';
export default class MatchMenuLivePlugin {
  opt: MatchUnitServiceOptions;
  lineups: HTMLElement[];
  squadlist: HTMLElement[];
  timeline: HTMLElement[];
  statistics: HTMLElement[];
  statsPollingUrl: string;
  eventsPollingUrl: string;

  statisticsAvailable = false;
  timelineAvailable = false;

  constructor(e: HTMLElement, o: MatchUnitServiceOptions) {
    // Default configuration properties.
    const defaults = {
      apiKey: '',
      interval: 5000,
    };

    this.opt = {
      ...defaults,
      ...o,
    };

    const dom = e;

    this.statsPollingUrl = formatUrl(matchStatsApiUrl, `v2/team-statistics/${this.opt.match.id}`);
    this.eventsPollingUrl = formatUrl(matchApiUrl, `v5/matches/${this.opt.match.id}/events`, {
      filter: EventsFilter.MAIN,
      offset: 0,
      limit: 1,
      order: Order.DESC,
    });

    this.lineups = Array.from(dom.querySelectorAll<HTMLElement>('.js-lineups'));
    this.squadlist = Array.from(dom.querySelectorAll<HTMLElement>('.js-squadlist'));
    this.timeline = Array.from(dom.querySelectorAll<HTMLElement>('.js-timeline'));
    this.statistics = Array.from(dom.querySelectorAll<HTMLElement>('.js-statistics'));
  }

  detach() {
    matchUnitServiceInstance.off(this.opt, this.onMatchUpdate, this);
    this.stopStatsPolling();
  }

  init() {
    matchUnitServiceInstance.on(this.opt, this.onMatchUpdate, this);
  }

  onMatchUpdate = (match: Match) => {
    if (matchStatisticsAvailable(match)) {
      !this.statisticsAvailable && !!this.statistics?.length && this.startStatsPolling();
      !this.timelineAvailable && !!this.timeline?.length && this.startEventsPolling();
    }

    const lineupsAvailable = matchLineupsAvailable(match);
    this.toggleLink(this.squadlist, !lineupsAvailable);
    this.toggleLink(this.lineups, lineupsAvailable && IS_MOBILE);
  };

  onStatsUpdate = (data: Statistic[]) => {
    this.statisticsAvailable = !!data.length;

    this.statisticsAvailable && this.stopStatsPolling();
    this.toggleLink(this.statistics, this.statisticsAvailable);
  };

  onEventsUpdate = (data: PlayerEvent[]) => {
    this.timelineAvailable = !!data.length;

    this.timelineAvailable && this.stopEventsPolling();
    this.toggleLink(this.timeline, this.timelineAvailable);
  };

  toggleLink(elements: HTMLElement[], active: boolean) {
    elements.forEach((element) => {
      element.classList.toggle('hidden', !active);
      const link = element.querySelector('a') ?? element;

      if (!link) return;
      link.dataset.href = link.dataset.href ?? link.getAttribute('href');
      link.setAttribute('href', active ? link.dataset.href : '#');
    });
  }

  startStatsPolling() {
    const interval = getRefreshRate(this.opt.match);
    LivePollingInstance.on(
      this.statsPollingUrl,
      [{ key: 'x-api-key', value: apiKey }],
      interval,
      this.onStatsUpdate,
      this,
    );
  }

  stopStatsPolling() {
    LivePollingInstance.off(this.statsPollingUrl, this.onStatsUpdate, this);
  }

  startEventsPolling() {
    const interval = getRefreshRate(this.opt.match);
    LivePollingInstance.on(
      this.eventsPollingUrl,
      [{ key: 'x-api-key', value: apiKey }],
      interval,
      this.onEventsUpdate,
      this,
    );
  }

  stopEventsPolling() {
    LivePollingInstance.off(this.eventsPollingUrl, this.onEventsUpdate, this);
  }
}
